import { useState, useEffect, useCallback } from "react";
import confetti from "canvas-confetti";
import { Trophy, Star, X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";

const RARITY_TIERS = [
  { max: 5, label: "Ultra Rare", color: "#FFD700", glow: "0 0 20px rgba(255, 215, 0, 0.6)", bg: "from-yellow-500/20 to-amber-600/10" },
  { max: 15, label: "Very Rare", color: "#A855F7", glow: "0 0 16px rgba(168, 85, 247, 0.5)", bg: "from-purple-500/20 to-violet-600/10" },
  { max: 30, label: "Rare", color: "#3B82F6", glow: "0 0 14px rgba(59, 130, 246, 0.4)", bg: "from-blue-500/15 to-cyan-600/10" },
  { max: 50, label: "Uncommon", color: "#10B981", glow: "0 0 10px rgba(16, 185, 129, 0.3)", bg: "from-emerald-500/10 to-green-600/5" },
  { max: 100, label: "Common", color: "#6B7280", glow: "none", bg: "from-gray-500/10 to-gray-600/5" },
];

function getRarityTier(rarity) {
  const pct = typeof rarity === "number" ? rarity : rarity?.currentPercentage ?? 100;
  return RARITY_TIERS.find((t) => pct <= t.max) || RARITY_TIERS[4];
}

function getTrophyColor(type) {
  if (!type) return null;
  const t = type.toLowerCase();
  if (t === "platinum") return "#E5E7EB";
  if (t === "gold") return "#FFD700";
  if (t === "silver") return "#C0C0C0";
  if (t === "bronze") return "#CD7F32";
  return null;
}

function fireConfetti() {
  const duration = 2500;
  const end = Date.now() + duration;

  const colors = ["#FFD700", "#A855F7", "#3B82F6", "#10B981", "#F59E0B", "#EF4444"];

  (function frame() {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors,
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

function fireBurst() {
  confetti({
    particleCount: 80,
    spread: 100,
    origin: { y: 0.5, x: 0.5 },
    colors: ["#FFD700", "#A855F7", "#3B82F6", "#10B981"],
    startVelocity: 30,
    gravity: 0.8,
  });
}

export default function AchievementCelebration({ achievements = [], onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);

  const current = achievements[currentIndex];
  const total = achievements.length;
  const tier = current ? getRarityTier(current.rarity) : RARITY_TIERS[4];
  const trophyColor = current ? getTrophyColor(current.trophyType) : null;

  useEffect(() => {
    if (achievements.length === 0) return;
    // Entrance
    requestAnimationFrame(() => setVisible(true));
    const t = setTimeout(() => {
      setCardVisible(true);
      fireConfetti();
    }, 200);
    return () => clearTimeout(t);
  }, [achievements.length]);

  useEffect(() => {
    // Burst on each new card
    if (cardVisible && achievements.length > 0) {
      fireBurst();
    }
  }, [currentIndex, cardVisible]);

  const goNext = useCallback(() => {
    if (currentIndex < total - 1) {
      setCardVisible(false);
      setTimeout(() => {
        setCurrentIndex((i) => i + 1);
        setCardVisible(true);
      }, 250);
    } else {
      handleClose();
    }
  }, [currentIndex, total]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCardVisible(false);
      setTimeout(() => {
        setCurrentIndex((i) => i - 1);
        setCardVisible(true);
      }, 250);
    }
  }, [currentIndex]);

  const handleClose = useCallback(() => {
    setCardVisible(false);
    setVisible(false);
    setTimeout(() => onClose?.(), 350);
  }, [onClose]);

  if (!achievements.length) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-300 ${
        visible ? "bg-black/80 backdrop-blur-md" : "bg-black/0 pointer-events-none"
      }`}
      onClick={handleClose}
      data-testid="achievement-celebration-overlay"
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors z-10"
        data-testid="celebration-close-btn"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Counter */}
      {total > 1 && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 text-white/50 text-sm font-medium tracking-wide">
          {currentIndex + 1} / {total}
        </div>
      )}

      {/* Achievement Card */}
      <div
        className={`relative max-w-sm w-full mx-4 transition-all duration-500 ease-out ${
          cardVisible
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-90 translate-y-8"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <div className="text-center mb-5">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Sparkles className="w-5 h-5" style={{ color: tier.color }} />
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: tier.color }}>
              Achievement Unlocked
            </span>
            <Sparkles className="w-5 h-5" style={{ color: tier.color }} />
          </div>
          {tier.label !== "Common" && (
            <span className="text-[10px] uppercase tracking-[0.15em] text-white/30">{tier.label}</span>
          )}
        </div>

        {/* Card */}
        <div
          className={`relative rounded-2xl border overflow-hidden bg-gradient-to-b ${tier.bg}`}
          style={{
            borderColor: `${tier.color}33`,
            boxShadow: tier.glow,
          }}
        >
          {/* Shimmer overlay for rare achievements */}
          {tier.label === "Ultra Rare" && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/5 to-transparent animate-pulse pointer-events-none" />
          )}

          <div className="p-6 flex flex-col items-center text-center relative">
            {/* Achievement Icon */}
            <div
              className="w-24 h-24 rounded-xl mb-4 overflow-hidden border-2 flex items-center justify-center bg-black/40"
              style={{ borderColor: `${tier.color}44` }}
            >
              {current?.imageUrl ? (
                <img
                  src={current.imageUrl}
                  alt={current.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className={`${current?.imageUrl ? "hidden" : "flex"} items-center justify-center w-full h-full`}
                style={{ display: current?.imageUrl ? "none" : "flex" }}
              >
                <Trophy className="w-10 h-10" style={{ color: tier.color }} />
              </div>
            </div>

            {/* Name */}
            <h3 className="text-white text-lg font-bold mb-1 leading-tight">{current?.name}</h3>

            {/* Description */}
            {current?.description && (
              <p className="text-white/50 text-sm mb-3 leading-relaxed max-w-xs">{current.description}</p>
            )}

            {/* Score / Trophy */}
            <div className="flex items-center gap-3">
              {current?.score > 0 && (
                <div className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1">
                  <Star className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-white text-sm font-semibold">{current.score}G</span>
                </div>
              )}
              {trophyColor && (
                <div className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1">
                  <Trophy className="w-3.5 h-3.5" style={{ color: trophyColor }} />
                  <span className="text-white/70 text-sm capitalize">{current.trophyType}</span>
                </div>
              )}
              {typeof current?.rarity === "number" || current?.rarity?.currentPercentage != null ? (
                <div className="bg-white/5 rounded-full px-3 py-1">
                  <span className="text-sm" style={{ color: tier.color }}>
                    {(typeof current.rarity === "number" ? current.rarity : current.rarity.currentPercentage).toFixed(1)}%
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Navigation */}
        {total > 1 && (
          <div className="flex items-center justify-between mt-4 px-2">
            <button
              onClick={goPrev}
              disabled={currentIndex === 0}
              className="flex items-center gap-1 text-sm text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-default transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <button
              onClick={goNext}
              className="flex items-center gap-1 text-sm font-medium transition-colors"
              style={{ color: tier.color }}
              data-testid="celebration-next-btn"
            >
              {currentIndex === total - 1 ? "Done" : "Next"} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Single achievement — tap to close */}
        {total === 1 && (
          <button
            onClick={handleClose}
            className="w-full mt-4 text-center text-sm text-white/30 hover:text-white/60 transition-colors"
          >
            Tap anywhere to close
          </button>
        )}
      </div>
    </div>
  );
}
